<template>
  <div class="mc-preview-image">
    <!-- Download and Close Button -->
    <div class="__top-button">
      <i
        @click="$emit('handleDownloadImg', activeAttachment.url, activeAttachment.name)"
        class="icon-outline_download prevent__close-preview"
      ></i>
      <i @click="handleClosePreview" class="icon-outline_close"></i>
    </div>

    <!-- Preview Image -->
    <div class="__preview-container">
      <b-button
        variant="outline-light"
        class="switch-img-btn prevent__close-preview"
        :disabled="isFistAttachment"
        @click="handleSwitchImage('previous')"
      >
        <i class="icon-outline_chevron-left prevent__close-preview"></i>
      </b-button>

      <div ref="img-ref" class="active-image">
        <img
          v-click-outside="vcoConfig"
          :style="{ transform }"
          :src="activeAttachment.url"
          :alt="`${activeAttachment.name}`"
        />
        <p>{{ activeAttachment.name }}</p>
      </div>

      <b-button
        variant="outline-light"
        class="switch-img-btn prevent__close-preview"
        :disabled="isLastAttachment"
        @click="handleSwitchImage('after')"
      >
        <i class="icon-outline_chevron-right prevent__close-preview"></i>
      </b-button>
    </div>

    <!-- Operation Button -->
    <div class="__operation">
      <div class="operation-btn">
        <template v-for="operation in operations">
          <b-button
            variant="primary"
            :key="operation.key"
            :class="`${operation.key}-btn prevent__close-preview`"
            @click="handleTransformImage(operation.key)"
          >
            <i class="prevent__close-preview" :class="operation.icon"></i>
          </b-button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    attachments: {
      type: Array,
      required: true,
    },
    activeAttachmentIdx: {
      type: Number,
    },
  },
  emits: ['handleDownloadImg'],
  data() {
    return {
      deg: 0,
      scale: 1,

      vcoConfig: {
        handler: this.handleClickOutsideImg,
        middleware: this.middlewareClickOutsideImg,
        events: ['dblclick', 'click'],
      },
    }
  },
  computed: {
    operations() {
      return [
        {
          key: 'maximize',
          icon: 'icon-outline_maximize',
        },
        {
          key: 'minimize',
          icon: 'icon-outline_minimize',
        },
        {
          key: 'rotate_left',
          icon: 'icon-outline_refresh',
        },
        {
          key: 'rotate_right',
          icon: 'icon-outline_refresh',
        },
      ]
    },
    activeAttachment() {
      return this.attachments[this.activeAttachmentIdx]
    },
    isFistAttachment() {
      return this.activeAttachmentIdx == 0
    },
    isLastAttachment() {
      return this.activeAttachmentIdx == this.attachments.length - 1
    },
    transform() {
      return `scale(${this.scale}, ${this.scale}) rotate(${this.deg}deg)`
    },
  },
  mounted() {
    document.addEventListener('keydown', (event) => {
      if (event.code == 'Escape') {
        this.handleClosePreview()
      }
    })
  },
  methods: {
    handleClickOutsideImg(_event) {
      this.handleClosePreview()
    },
    middlewareClickOutsideImg(event) {
      return !event.target.className.includes('prevent__close-preview')
    },
    handleClosePreview() {
      this.$emit('update:activeAttachmentIdx', null)
    },
    handleSwitchImage(key) {
      if (key == 'previous' && !this.isFistAttachment) {
        this.$emit('update:activeAttachmentIdx', this.activeAttachmentIdx - 1)
      } else if (key == 'after' && !this.isLastAttachment) {
        this.$emit('update:activeAttachmentIdx', this.activeAttachmentIdx + 1)
      }

      this.deg = 0
      this.scale = 1
    },
    handleTransformImage(key) {
      switch (key) {
        case 'maximize':
          if (this.scale != 1.5) {
            this.scale = this.scale + 0.5
          }
          break

        case 'minimize':
          if (this.scale != 0.5) {
            this.scale = this.scale - 0.5
          }
          break

        case 'rotate_left':
          this.deg = this.deg - 90
          break

        case 'rotate_right':
          this.deg = this.deg + 90
          break

        default:
          break
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-preview-image {
  position: fixed;
  z-index: 9999;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(1, 19, 61, 0.4);

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .__top-button {
    display: flex;
    justify-content: flex-end;
    gap: 20px;
    margin: 20px 28px;

    i {
      cursor: pointer;
    }

    i::before {
      font-size: 20px;
      color: #ffffff;
    }
  }

  .__preview-container {
    display: flex;
    justify-content: space-between;

    .switch-img-btn {
      border: none;

      i::before {
        font-size: 49px;
        color: #ffffff;
      }

      &:hover {
        background-color: transparent;
      }

      &:disabled {
        cursor: default;

        i::before {
          color: transparent;
        }
      }
    }

    .active-image {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 20px;
      color: #ffffff;

      img {
        max-width: 50vw;
        height: 50vh;
        box-shadow: 0px 15px 50px 0px rgba(1, 19, 61, 0.6);
        transition: transform 0.5s ease-in-out;
      }
    }
  }

  .__operation {
    display: flex;
    justify-content: center;
    margin: 20px auto;
    width: fit-content;

    .operation-btn {
      display: flex;
      gap: 0;

      button {
        border-radius: 0px;
        padding: 8px;

        i::before {
          color: #ffffff;
          font-size: 20px;
        }

        &.maximize-btn {
          border-radius: 4px 0px 0px 4px;
        }

        &.rotate_left-btn {
          -webkit-transform: scaleX(-1);
          transform: scaleX(-1);
        }

        &.rotate_right-btn {
          border-radius: 0px 4px 4px 0px;
        }
      }
    }
  }
}
</style>
